import {
  augmentHeadlands,
  augmentRoads,
  getSecondaryHeadlandFeature,
} from '../utils/layerAugmenters';
import { CUSTOM_LAYER_TYPE, DEFAULT_LAYER_WIDTH_FEET } from './layers';
import { AREA_UNITS, DISTANCE_UNITS, SMALL_AREA_UNITS } from './units';
import BuildingIcon from '../components/icons/BuildingIcon';
import CircleIcon from '../components/icons/CircleIcon';
import FencesIcon from '../components/icons/FencesIcon';
import HeadlandsIcon from '../components/icons/HeadlandsIcon';
import MountainIcon from '../components/icons/MountainIcon';
import PipesIcon from '../components/icons/PipesIcon';
import PlugIcon from '../components/icons/PlugIcon';
import RoadsIcon from '../components/icons/RoadsIcon';
import { RidgeIcon, ValleyIcon } from '../components/icons/DottedCurveIcon';
import FarmBoundaryIcon from '../components/icons/FarmBoundaryIcon';
import { copyFeatureWithoutProperties, getHeadlandArea } from '../utils/layers';
import CluBoundaryIcon from '../components/icons/CluBoundaryIcon';
import FieldBoundaryIcon from '../components/icons/FieldBoundaryIcon';
import PencilIcon from '../components/icons/PencilIcon';
import SoilIcon from '../components/icons/SoilIcon';

export const EARTH_ZOOM = 2;

export const largeCropPointRadius = [
  'interpolate',
  ['linear'],
  ['zoom'],
  0,
  0.25,
  12,
  0.5,
  15,
  1.5,
  18,
  2.25,
  20,
  6 * 0.75,
  22,
  8 * 0.75,
];

export const smallCropPointRadius = [
  'interpolate',
  ['linear'],
  ['zoom'],
  0,
  0.5,
  15,
  1,
  18,
  1.5,
  20,
  3,
  22,
  4,
];

export const selectedRowPointRadius = [
  'interpolate',
  ['linear'],
  ['zoom'],
  0,
  0.5,
  12,
  1,
  15,
  3,
  18,
  4.5,
  20,
  6 * 1.5,
  22,
  8 * 1.5,
];

export const rotationCenterRadius = [
  'interpolate',
  ['linear'],
  ['zoom'],
  12,
  3,
  20,
  8,
];

// Circle sizes for points in custom layers
export const smallPointRadius = 4;
export const largePointRadius = 6;

// satellite is a layer set by mapbox, referring to mapbox's global satellite view.
export const SATELLITE_LAYER_ID = 'satellite';

export const BOTTOM_LAYER_ABOVE_SATELLITE = 'tunnel-minor-case';

export const CONTOUR_LAYER_ID = 'mapbox-contour-layer';
export const CONTOUR_OUTLINE_ID = 'mapbox-contour-outline';
export const CONTOUR_LABELS_ID = 'mapbox-contour-labels';

export const CONTOUR_LAYERS = [
  CONTOUR_LABELS_ID,
  CONTOUR_LAYER_ID,
  CONTOUR_OUTLINE_ID,
];

export const CLU_BOUNDARIES_LAYER_ID = 'CLU_BOUNDARIES';
export const SOIL_BOUNDARIES_LAYER_ID = 'SOIL_BOUNDARIES';

// This determines the type of geometry that can be imported for the layer via KML.
export const GEOMETRY_TYPES = {
  LINE: 'line',
  POLYGON: 'polygon',
};

/**
 * Map layers, ordered by view index (last is on top).
 */
export const mapLayers = {
  UNDER_FILL: 'underFill', // User layers that go under the fill layer.
  FILL: 'fill', // A darkened fill background for a section.
  // User layers that go over the fill layer.
  // This is the default for user layers that don't specify a mapIndexLayer.
  OVER_FILL: 'overFill',
  TILESET: 'tileset', // the suitability or characteristic raster layer
  ROWS: 'rows', // All rows in a section.
  FIELD_FEATURE: 'fieldFeature', // The field polygon or lineString.
  POINTS: 'points', // Crop points in a section.
  // A debug layer for showing all points generated in generate points and rows,
  // including those that are outside the section mask.
  ALL_POINTS: 'allPoints',
  // Debug layers for showing specified points and rows. These can be configured
  // by adding to debugPoints/debugRows in addPointsAndRows.js
  DEBUG_POINTS: 'debugPoints',
  DEBUG_ROWS: 'debugRows',
  // Highlights the inflection points of the row that is hovered in the left menu.
  ACTIVE_ROW: 'activeRow',
  // Highlights the active row if it only has a single point.
  ACTIVE_ROW_POINT: 'activeRowPoint',
  CUSTOM_LAYER_POINTS: 'customLayerPoints', // A layer for points in custom user layers. This puts them on top of sections for improved visibility.
  // Arrow to show the direction of the row (only on active row).
  ACTIVE_ROW_ARROW: 'activeRowArrow',
  // The currently selected row when a row is engaged. Different
  // from ACTIVE_ROW because we don't want to obscure the crop points
  // while editing the row overrides.
  SELECTED_ROW: 'selectedRow',
  ACTIVE_LAYER_FEATURE: 'activeLayerFeature', // The currently selected layer feature.
  // Icon displayed while offsetting or rotating a section
  CENTER_ICON: 'centerIcon',
  // The rotation center point of the section, shown in rotation mode.
  CENTER: 'center',
  NAME: 'name', // A name label for the section.
};

export const customLayerSpec = {
  layerType: CUSTOM_LAYER_TYPE,
  // NOTE: no geometryType set since custom layers have no geometry restrictions.
  drawTool: 'polygon', // The default value when a user enters edit mode on a custom layer (also sometimes we use `drawTool` to determine if a layer can be edited).
  iconComponent: <PencilIcon />,
};

export const layerGroups = {
  BASE: 'Base Information',
  BOUNDARIES: 'Boundaries',
  INFRASTRUCTURE: 'Infrastructure',
  TOPOGRAPHIC: 'Topographic Features',
};

// NOTE: the keys of this object must be kept in sync with `LayerTypes` in `layer_types.rb`
export const userLayers = {
  HEADLANDS: {
    displayName: 'Headlands',
    displayGroup: layerGroups.BOUNDARIES,
    geometryType: GEOMETRY_TYPES.POLYGON,
    drawTool: 'polygon',
    getArea: getHeadlandArea,
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    featureToFieldFeature: (feature, width) =>
      copyFeatureWithoutProperties(
        feature.properties.isInnerFeature
          ? feature
          : getSecondaryHeadlandFeature(feature, width),
      ),
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#00b001',
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12,
          0.25,
          15,
          1,
          18,
          2,
        ],
      },
    },
    width: DEFAULT_LAYER_WIDTH_FEET,
    augment: augmentHeadlands,
    iconComponent: <HeadlandsIcon />,
  },
  ROADS: {
    displayName: 'Roads',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.LINE,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': [
          'match',
          ['get', 'style'],
          'center',
          '#4DFD4E',
          '#00b001',
        ],
        'line-width': 1.5,
      },
    },
    width: DEFAULT_LAYER_WIDTH_FEET,
    augment: augmentRoads,
    iconComponent: <RoadsIcon />,
  },
  UTILITIES: {
    displayName: 'Utilities',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.LINE,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['match', ['get', 'style'], 'center', '#FFF', '#AAA'],
        'line-width': 1.5,
      },
    },
    width: DEFAULT_LAYER_WIDTH_FEET,
    augment: augmentRoads,
    iconComponent: <PlugIcon />,
  },
  CONTOURS: {
    displayName: 'Contours',
    displayGroup: layerGroups.TOPOGRAPHIC,
    geometryType: GEOMETRY_TYPES.LINE,
    mapIndexLayer: mapLayers.UNDER_FILL,
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#ccc',
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.2, 15, 0.5],
      },
    },
    iconComponent: <MountainIcon />,
    // Don't fetch contours initially (slow networks and large layer files)
    shown: false,
  },
  RIDGES: {
    displayName: 'Ridges',
    displayGroup: layerGroups.TOPOGRAPHIC,
    geometryType: GEOMETRY_TYPES.LINE,
    mapIndexLayer: mapLayers.UNDER_FILL,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#bd3b08',
        'line-width': 3,
      },
    },
    iconComponent: <RidgeIcon />,
  },
  VALLEYS: {
    displayName: 'Valleys',
    displayGroup: layerGroups.TOPOGRAPHIC,
    geometryType: GEOMETRY_TYPES.LINE,
    mapIndexLayer: mapLayers.UNDER_FILL,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#4fb1db',
        'line-width': 3,
      },
    },
    iconComponent: <ValleyIcon />,
  },
  PIPING: {
    displayName: 'Piping',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.LINE,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#007bbd',
        'line-width': 3,
      },
    },
    iconComponent: <PipesIcon />,
  },
  FENCES: {
    displayName: 'Fences',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.LINE,
    drawTool: 'line_string',
    units: { unitSpec: DISTANCE_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#8c1545',
        'line-width': 1.5,
      },
    },
    iconComponent: <FencesIcon />,
  },
  BOUNDARY: {
    displayName: 'Farm Boundary',
    displayGroup: layerGroups.BOUNDARIES,
    geometryType: GEOMETRY_TYPES.POLYGON,
    drawTool: 'polygon',
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#cf6911',
        'line-width': 3,
      },
    },
    iconComponent: <FarmBoundaryIcon />,
  },
  PONDS: {
    displayName: 'Ponds',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.POLYGON,
    drawTool: 'polygon',
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    paintProperties: {
      type: 'fill',
      layout: {},
      paint: {
        'fill-color': '#007bbd',
        'fill-opacity': 0.4,
      },
    },
    iconComponent: <CircleIcon />,
  },
  BUILDINGS: {
    displayName: 'Buildings',
    displayGroup: layerGroups.INFRASTRUCTURE,
    geometryType: GEOMETRY_TYPES.POLYGON,
    drawTool: 'polygon',
    units: { unitSpec: SMALL_AREA_UNITS, turf: 'feet' },
    paintProperties: {
      type: 'fill',
      layout: {},
      paint: {
        'fill-color': '#eb4034',
        'fill-opacity': 0.6,
      },
    },
    iconComponent: <BuildingIcon />,
  },
  FIELD_BOUNDARIES: {
    displayName: 'Field Boundaries',
    displayGroup: layerGroups.BOUNDARIES,
    geometryType: GEOMETRY_TYPES.POLYGON,
    drawTool: 'polygon',
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    featureToFieldFeature: copyFeatureWithoutProperties,
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#F29E5B',
        'line-width': 2,
      },
    },
    iconComponent: <FieldBoundaryIcon />,
  },
  [CLU_BOUNDARIES_LAYER_ID]: {
    displayName: 'CLU Boundaries (U.S.)',
    displayGroup: layerGroups.BOUNDARIES,
    geometryType: null,
    disableUserEdits: true,
    drawTool: 'polygon', // Used for showing features and units, but not actually editable.
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    featureToFieldFeature: copyFeatureWithoutProperties,
    paintProperties: {
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#550000',
        'line-width': 2,
      },
    },
    iconComponent: <CluBoundaryIcon />,
  },
  [SOIL_BOUNDARIES_LAYER_ID]: {
    displayName: 'Soil Types & Attributes (U.S.)',
    displayGroup: layerGroups.BASE,
    geometryType: null,
    mapIndexLayer: mapLayers.UNDER_FILL,
    disableUserEdits: true,
    units: { unitSpec: AREA_UNITS, turf: 'acres' },
    paintProperties: {
      type: 'fill',
      layout: {},
      paint: {
        'fill-color': [
          'case',
          ['!=', ['get', 'rgbString'], null],
          ['get', 'rgbString'],
          '#926829',
        ],
        'fill-opacity': 0.6,
      },
    },
    iconComponent: <SoilIcon />,
    // Don't fetch soil boundaries immediately because they obscure map
    shown: false,
  },
};

export const layerTypesWithShortcuts = [
  'ROADS',
  'UTILITIES',
  'HEADLANDS',
  'PIPING',
  'FENCES',
  'BUILDINGS',
];

export const mapModes = {
  // Section modes
  // When adding a single section mode, add to singleSectionModes below as well
  SECTION_ENGAGED: 'section engaged', // Show section menu with no map tools engaged
  ROTATE: 'rotate', // Rotate the crop points within the section by dragging the mouse
  EDIT: 'edit', // Edit an existing section
  DRAW: 'draw', // Remove the current section if there is one and draw a new section
  OFFSET: 'offset', // Use the arrow keys to offset the crop points in a section
  DELETING: 'deleting', // Confirmation for deleting a section
  MULTI_SECTION_SELECT: 'multi section select', // Select multiple sections
  // Section group modes
  SECTION_GROUP_ENGAGED: 'section group engaged', // Show section group menu
  // Row modes
  ROW_ENGAGED: 'row engaged', // Show row menu with no map tools engaged
  // Layer modes
  DRAW_LAYER: 'draw layer', // Engage the layer editor with the draw tool engaged
  EDIT_LAYER: 'edit layer', // Engage the layer editor with no tool engaged
  LAYER_ENGAGED: 'layer engaged', // Show layer menu with no map tools engaged
  // Layer feature modes
  LAYER_FEATURE_ENGAGED: 'layer feature engaged', // Show layer feature menu with no map tools engaged
  MULTI_LAYER_FEATURE_SELECT: 'multi layer feature select', // Select multiple layer features
  EDIT_LAYER_FEATURE: 'edit layer feature', // Engage the layer feature editor
  DELETING_LAYER_FEATURE: 'deleting layer feature', // Confirmation for deleting a layer feature
  // View modes
  VIEW: 'view', // Show the farm summary
  // Measurement
  MEASUREMENT: 'measurement', // Measure distances and areas
};

export const singleSectionModes = [
  mapModes.SECTION_ENGAGED,
  mapModes.ROTATE,
  mapModes.EDIT,
  mapModes.DRAW,
  mapModes.OFFSET,
  mapModes.DELETING,
];

export const measurementModes = {
  DISTANCE: 'distance',
  AREA: 'area',
};

export const NO_SATELLITE_BACKGROUND = 'blank-background';
export const STREET_LINES_LAYER_ID = 'mapbox-streets-layer';
export const WATERWAYS_LAYER_ID = 'mapbox-waterways-layer';
export const WATER_BODIES_LAYER_ID = 'mapbox-waterbodies-layer';

export const WATER_LAYERS = [WATERWAYS_LAYER_ID, WATER_BODIES_LAYER_ID];

export const BACKGROUND_COLORS = {
  light: '#DBDBDC',
  dark: '#636363',
};

export const NO_SATELLITE_LAYERS = [NO_SATELLITE_BACKGROUND, ...WATER_LAYERS];

// markers in mapbox's naming conventions that denote a layer that creates or labels a road-like thing
export const STREETS_MARKERS = ['street', 'road', 'tunnel', 'bridge'];

export const TILESET_RASTER_OPACITY = 0.75;
export const TILESET_RASTER_RESAMPLING = 'linear';

// See https://github.com/mapbox/mapbox-gl-js/blob/fd2045fd6aef55d9265c9a4c43fb48b294289482/src/style/style.js#L1400
export const MAPBOX_SOURCECACHE_LAYER_PREFIX = 'other:';
